import { BiReportsTracking } from "@/lib/tracking/biReports";
import { FlexListSummary } from "@/components/listSummary";
import { FilterApplicableFor } from "@/components/tags/types";
import { ColorInsightTags, FilterTagWrapper } from "@/components/tags";
import { Card, CardContent, CardHeader } from "@altimateai/lego";
import useFiltersBySearchParams from "@/helpers/useFiltersBySearchParams";
import { DataTable } from "@/components/tables";
import { Paginate } from "@/components/paginate";
import { BiReport } from "./types";
import { useQuery } from "@tanstack/react-query";
import {
  GET_BI_DASHBOARDS_FILTERS,
  GET_BI_DASHBOARDS_SUMMARY,
  GET_DASHBOARDS,
} from "@/pages/query/constants";
import {
  getBiDashboardsPaginated,
  getBiDashboardsSummary,
  downloadDashboardsCSV,
  getBiReportFilters,
} from "@/helpers/apis";
import { Link } from "react-router-dom";
import { useBreadcrumb } from "@/components/breadcrumbs";
import { FilterType, parseSortDirection } from "@/components/tables/types";
import { Stack } from "@altimateai/lego";
import SavedFilters from "@/components/tags/SavedFilters";
import { RelativeComponentLoader } from "@/components/loader";
import { SiTableau } from "react-icons/si";
import { DownloadTable } from "@/components/downloadTable";
import tagStyles from "@/components/tags/styles.module.scss";
import AddInstrumentationButton from "./components/AddInstrumentationButton";
import { DateRange, StaticDateRangeKeys } from "@/components/dateRange";
import { useDateFromSearchParams } from "@/helpers/useTimeHooks";
import dayjs from "dayjs";
import {
  getDateByEndOfDayWithoutTimeoffset,
  getDateByStartOfDayWithoutTimeoffset,
} from "@/components/dateRange/utils";
import BIUserReportInsightOrOpportunityTag, {
  insightToTagsTooltipDisplayFormat,
  opportunityToTagsTooltipDisplayFormat,
} from "@/pages/biReports/BIUserReportInsightOrOpportunityTag";

// Search parameter constants
const SEARCH_PARAMS = {
  PAGE: "page",
  PAGE_SIZE: "page_size",
  DASHBOARD: "dashboard",
  WORKBOOK: "workbook",
  OWNER: "owner",
  SITE_NAME: "site_name",
  INSTANCE_NAME: "instance_name",
  TABLEAU_TAGS: "tableau_tags",
  INSIGHTS: "insights",
  OPPORTUNITY_NAME: "opportunity_name",
  SORT_BY: "sort_by",
  SORT: "sort",
  START_DATE: "start_date",
  END_DATE: "end_date",
} as const;

const BiReportsList = () => {
  useBreadcrumb("Dashboards", {
    defaultParents: [{ label: "Insights", pathname: "/bi-reports", noLink: true }],
    truncateTo: "/bi-reports",
  });
  const { searchParams, updateSearchParamsByKey } = useFiltersBySearchParams();
  const sortBy = searchParams.get(SEARCH_PARAMS.SORT_BY) || "views";
  const sortOrder = parseSortDirection(searchParams.get(SEARCH_PARAMS.SORT), "desc");
  const { startDate, endDate } = useDateFromSearchParams(
    dayjs().subtract(90, "day").toDate(),
    dayjs().toDate()
  );

  const { data: paginationResponse, isFetching: isLoading } = useQuery({
    queryKey: [
      GET_DASHBOARDS,
      searchParams.get(SEARCH_PARAMS.PAGE),
      searchParams.get(SEARCH_PARAMS.PAGE_SIZE),
      searchParams.getAll(SEARCH_PARAMS.DASHBOARD),
      searchParams.getAll(SEARCH_PARAMS.WORKBOOK),
      searchParams.getAll(SEARCH_PARAMS.OWNER),
      searchParams.getAll(SEARCH_PARAMS.SITE_NAME),
      searchParams.getAll(SEARCH_PARAMS.INSTANCE_NAME),
      searchParams.getAll(SEARCH_PARAMS.TABLEAU_TAGS),
      searchParams.getAll(SEARCH_PARAMS.INSIGHTS),
      searchParams.getAll(SEARCH_PARAMS.OPPORTUNITY_NAME),
      sortBy,
      sortOrder,
      startDate,
      endDate,
    ],
    queryFn: () =>
      getBiDashboardsPaginated({
        page: parseInt(searchParams.get(SEARCH_PARAMS.PAGE) || "1") || 1,
        page_size: parseInt(searchParams.get(SEARCH_PARAMS.PAGE_SIZE) || "20") || 20,
        dashboard: searchParams.getAll(SEARCH_PARAMS.DASHBOARD) || [],
        owner: searchParams.getAll(SEARCH_PARAMS.OWNER) || [],
        workbook: searchParams.getAll(SEARCH_PARAMS.WORKBOOK) || [],
        site_name: searchParams.getAll(SEARCH_PARAMS.SITE_NAME) || [],
        instance_name: searchParams.getAll(SEARCH_PARAMS.INSTANCE_NAME) || [],
        tableau_tags: searchParams.getAll(SEARCH_PARAMS.TABLEAU_TAGS) || [],
        insights: searchParams.getAll(SEARCH_PARAMS.INSIGHTS) || [],
        opportunity_name: searchParams.getAll(SEARCH_PARAMS.OPPORTUNITY_NAME) || [],
        sort_by: sortBy,
        sort: sortOrder,
        start_date: startDate
          ? getDateByStartOfDayWithoutTimeoffset(startDate)?.toISOString()
          : undefined,
        end_date: endDate ? getDateByEndOfDayWithoutTimeoffset(endDate)?.toISOString() : undefined,
      }),
  });

  const { data: reportsFilters, isFetching: isReportsFiltersLoading } = useQuery({
    queryKey: [GET_BI_DASHBOARDS_FILTERS],
    queryFn: () => getBiReportFilters(),
  });

  const { data: flexListSummary, isFetching: isDashboardSummaryLoading } = useQuery({
    queryKey: [
      GET_BI_DASHBOARDS_SUMMARY,
      startDate,
      endDate,
      searchParams.getAll(SEARCH_PARAMS.SITE_NAME),
      searchParams.getAll(SEARCH_PARAMS.INSTANCE_NAME),
    ],
    queryFn: () =>
      getBiDashboardsSummary({
        start_date: startDate
          ? getDateByStartOfDayWithoutTimeoffset(startDate)?.toISOString()
          : undefined,
        end_date: endDate ? getDateByEndOfDayWithoutTimeoffset(endDate)?.toISOString() : undefined,
        site_name: searchParams.getAll(SEARCH_PARAMS.SITE_NAME) || [],
        instance_name: searchParams.getAll(SEARCH_PARAMS.INSTANCE_NAME) || [],
      }),
  });

  const handleDownload = async () => {
    await downloadDashboardsCSV({
      sort_by: sortBy,
      sort: sortOrder,
      dashboard: searchParams.getAll(SEARCH_PARAMS.DASHBOARD),
      workbook: searchParams.getAll(SEARCH_PARAMS.WORKBOOK),
      owner: searchParams.getAll(SEARCH_PARAMS.OWNER),
      tableau_tags: searchParams.getAll(SEARCH_PARAMS.TABLEAU_TAGS),
      insights: searchParams.getAll(SEARCH_PARAMS.INSIGHTS),
      site_name: searchParams.getAll(SEARCH_PARAMS.SITE_NAME),
      instance_name: searchParams.getAll(SEARCH_PARAMS.INSTANCE_NAME),
      opportunity_name: searchParams.getAll(SEARCH_PARAMS.OPPORTUNITY_NAME),
      start_date: startDate
        ? getDateByStartOfDayWithoutTimeoffset(startDate)?.toISOString()
        : undefined,
      end_date: endDate ? getDateByEndOfDayWithoutTimeoffset(endDate)?.toISOString() : undefined,
    });
  };

  return (
    <div>
      {!isDashboardSummaryLoading && (
        <FlexListSummary
          summary={(flexListSummary || []).map(item => ({
            ...item,
            value: String(item.value),
          }))}
        />
      )}

      <div className="al-mt-4">
        <Card>
          <CardHeader className={"al-flex al-flex-col"}>
            <Stack gap="md" className={"al-self-end"}>
              <DateRange
                startDate={startDate}
                endDate={endDate}
                availableStaticRanges={[
                  StaticDateRangeKeys.LAST_30_DAYS,
                  StaticDateRangeKeys.LAST_3_MONTHS,
                  StaticDateRangeKeys.LAST_6_MONTHS,
                ]}
                disableCalendarSelection
                onDateRangeSelect={(s, e) => {
                  updateSearchParamsByKey({
                    [SEARCH_PARAMS.START_DATE]: s?.toISOString(),
                    [SEARCH_PARAMS.END_DATE]: e?.toISOString(),
                  });
                }}
              />
              <DownloadTable handleDownload={handleDownload} />
              <SavedFilters applicableFor={FilterApplicableFor.BI_REPORTS} />
            </Stack>
            <FilterTagWrapper
              applicableFor={FilterApplicableFor.BI_REPORTS}
              filters={{
                dashboardFilter: {
                  filterStr: searchParams.getAll(SEARCH_PARAMS.DASHBOARD) || [],
                  label: "Dashboard",
                  searchKey: SEARCH_PARAMS.DASHBOARD,
                  onclose: (removedFilter?: string) => {
                    updateSearchParamsByKey({
                      [SEARCH_PARAMS.DASHBOARD]: searchParams
                        .getAll(SEARCH_PARAMS.DASHBOARD)
                        .filter(d => d !== removedFilter),
                    });
                  },
                },
                workbookFilter: {
                  filterStr: searchParams.getAll(SEARCH_PARAMS.WORKBOOK) || [],
                  label: "Workbook",
                  searchKey: SEARCH_PARAMS.WORKBOOK,
                  onclose: (removedFilter?: string) => {
                    updateSearchParamsByKey({
                      [SEARCH_PARAMS.WORKBOOK]: searchParams
                        .getAll(SEARCH_PARAMS.WORKBOOK)
                        .filter(d => d !== removedFilter),
                    });
                  },
                },
                ownerFilter: {
                  filterStr: searchParams.getAll(SEARCH_PARAMS.OWNER) || [],
                  label: "Owner",
                  searchKey: SEARCH_PARAMS.OWNER,
                  onclose: (removedFilter?: string) => {
                    updateSearchParamsByKey({
                      [SEARCH_PARAMS.OWNER]: searchParams
                        .getAll(SEARCH_PARAMS.OWNER)
                        .filter(d => d !== removedFilter),
                    });
                  },
                },
                siteNameFilter: {
                  filterStr: searchParams.getAll(SEARCH_PARAMS.SITE_NAME) || [],
                  label: "Site Name",
                  searchKey: SEARCH_PARAMS.SITE_NAME,
                  onclose: (removedFilter?: string) => {
                    updateSearchParamsByKey({
                      [SEARCH_PARAMS.SITE_NAME]: searchParams
                        .getAll(SEARCH_PARAMS.SITE_NAME)
                        .filter(d => d !== removedFilter),
                    });
                  },
                },

                instanceNameFilter: {
                  filterStr: searchParams.getAll(SEARCH_PARAMS.INSTANCE_NAME) || [],
                  label: "Instance Name",
                  searchKey: SEARCH_PARAMS.INSTANCE_NAME,
                  onclose: (removedFilter?: string) => {
                    updateSearchParamsByKey({
                      [SEARCH_PARAMS.INSTANCE_NAME]: searchParams
                        .getAll(SEARCH_PARAMS.INSTANCE_NAME)
                        .filter(d => d !== removedFilter),
                    });
                  },
                },
                tableauTagsFilter: {
                  filterStr: searchParams.getAll(SEARCH_PARAMS.TABLEAU_TAGS) || [],
                  label: "Tableau Tags",
                  searchKey: SEARCH_PARAMS.TABLEAU_TAGS,
                  onclose: (removedFilter?: string) => {
                    updateSearchParamsByKey({
                      [SEARCH_PARAMS.TABLEAU_TAGS]: searchParams
                        .getAll(SEARCH_PARAMS.TABLEAU_TAGS)
                        .filter(d => d !== removedFilter),
                    });
                  },
                },
                insightsFilter: {
                  filterStr: searchParams.getAll(SEARCH_PARAMS.INSIGHTS) || [],
                  label: "Insights",
                  searchKey: SEARCH_PARAMS.INSIGHTS,
                  onclose: (removedFilter?: string) => {
                    updateSearchParamsByKey({
                      [SEARCH_PARAMS.INSIGHTS]: searchParams
                        .getAll(SEARCH_PARAMS.INSIGHTS)
                        .filter(d => d !== removedFilter),
                    });
                  },
                },
                opportunityNameFilter: {
                  filterStr: searchParams.getAll(SEARCH_PARAMS.OPPORTUNITY_NAME) || [],
                  label: "Opportunity",
                  searchKey: SEARCH_PARAMS.OPPORTUNITY_NAME,
                  onclose: (removedFilter?: string) => {
                    updateSearchParamsByKey({
                      [SEARCH_PARAMS.OPPORTUNITY_NAME]: searchParams
                        .getAll(SEARCH_PARAMS.OPPORTUNITY_NAME)
                        .filter(d => d !== removedFilter),
                    });
                  },
                },
              }}
            />
          </CardHeader>
          <CardContent>
            {isLoading && (
              <Stack align={"center"} justify={"center"} className={"al-w-full al-min-h-72"}>
                <RelativeComponentLoader label={"Loading..."} />
              </Stack>
            )}
            {!isLoading && paginationResponse && (
              <>
                <DataTable<BiReport>
                  header={[
                    {
                      label: "Dashboard",
                      uid: "dashboard",
                      render: (item: BiReport) => (
                        <Stack gap={"sm"} className={"al-text-brand"}>
                          <SiTableau />
                          <Link
                            to={`/bi-reports/${encodeURIComponent(item.rk)}`}
                            state={{ entityName: item.dashboard }}
                            className="al-text-xs al-font-normal al-break-all al-cursor-pointer"
                            onClick={() => BiReportsTracking.detailViewed(item.rk)}
                          >
                            {item.dashboard}
                          </Link>
                        </Stack>
                      ),
                      id: 0,
                      width: 220,
                      filter: !isReportsFiltersLoading
                        ? {
                            filterType: FilterType.DROPDOWN,
                            searchKey: SEARCH_PARAMS.DASHBOARD,
                            value: searchParams.getAll(SEARCH_PARAMS.DASHBOARD) || [],
                            options: reportsFilters?.dashboard
                              ? reportsFilters.dashboard.map((d: string) => ({
                                  label: d,
                                  value: d,
                                }))
                              : [],
                            onChange: (value: string[]) => {
                              updateSearchParamsByKey({
                                [SEARCH_PARAMS.DASHBOARD]: value,
                                [SEARCH_PARAMS.PAGE]: "1",
                              });
                            },
                          }
                        : undefined,
                    },
                    {
                      label: "Owner",
                      uid: "owner",
                      render: (item: BiReport) => (
                        <span className="al-text-xs al-text-gray-800 al-font-normal">
                          {item.owner}
                        </span>
                      ),
                      id: 1,
                      width: 250,
                      filter: !isReportsFiltersLoading
                        ? {
                            filterType: FilterType.DROPDOWN,
                            searchKey: SEARCH_PARAMS.OWNER,
                            value: searchParams.getAll(SEARCH_PARAMS.OWNER) || [],
                            options: reportsFilters?.owner
                              ? reportsFilters.owner.map((o: string) => ({
                                  label: o,
                                  value: o,
                                }))
                              : [],
                            onChange: (value: string[]) => {
                              updateSearchParamsByKey({
                                [SEARCH_PARAMS.OWNER]: value,
                                [SEARCH_PARAMS.PAGE]: "1",
                              });
                            },
                          }
                        : undefined,
                    },
                    {
                      label: "Insights",
                      uid: "insights",
                      width: 200,
                      render: (item: BiReport) => (
                        <div className={tagStyles.tags}>
                          {(item?.insights || []).map((insight, index) => (
                            <BIUserReportInsightOrOpportunityTag
                              key={index}
                              insightOrOpportunity={insightToTagsTooltipDisplayFormat(insight)}
                            />
                          ))}
                        </div>
                      ),
                      id: 2,
                      filter: !isReportsFiltersLoading
                        ? {
                            filterType: FilterType.DROPDOWN,
                            searchKey: SEARCH_PARAMS.INSIGHTS,
                            value: searchParams.getAll(SEARCH_PARAMS.INSIGHTS) || [],
                            options: reportsFilters?.insights
                              ? reportsFilters.insights.map((insight: string) => ({
                                  label: insight,
                                  value: insight,
                                }))
                              : [],
                            onChange: (value: string[]) => {
                              updateSearchParamsByKey({
                                [SEARCH_PARAMS.INSIGHTS]: value,
                                [SEARCH_PARAMS.PAGE]: "1",
                              });
                            },
                          }
                        : undefined,
                    },
                    {
                      label: "Opportunities",
                      uid: "opportunities",
                      width: 250,
                      tooltipInfo: "Cost saving opportunities seen over the past month",
                      render: (item: BiReport) => (
                        <div className={tagStyles.tags}>
                          {(item?.opportunities || []).map((opportunity, index) => (
                            <BIUserReportInsightOrOpportunityTag
                              key={index}
                              insightOrOpportunity={opportunityToTagsTooltipDisplayFormat(
                                opportunity
                              )}
                            />
                          ))}
                        </div>
                      ),
                      id: 3,
                      filter: !isReportsFiltersLoading
                        ? {
                            filterType: FilterType.DROPDOWN,
                            searchKey: SEARCH_PARAMS.OPPORTUNITY_NAME,
                            value: searchParams.getAll(SEARCH_PARAMS.OPPORTUNITY_NAME) || [],
                            options: reportsFilters?.opportunity_name
                              ? reportsFilters.opportunity_name.map((opportunityName: string) => ({
                                  label: opportunityName
                                    .replace(/_/g, " ")
                                    .replace(/\b\w/g, (l: string) => l.toUpperCase()),
                                  value: opportunityName,
                                }))
                              : [],
                            onChange: (value: string[]) => {
                              updateSearchParamsByKey({
                                [SEARCH_PARAMS.OPPORTUNITY_NAME]: value,
                                [SEARCH_PARAMS.PAGE]: "1",
                              });
                            },
                          }
                        : undefined,
                    },
                    {
                      label: "Total Cost ($)",
                      uid: "total_cost",
                      render: (item: BiReport) => (
                        <span className="al-text-xs al-text-gray-800 al-font-normal">
                          {item.total_cost !== null && item.total_cost !== undefined ? (
                            `$${item.total_cost.toFixed(2)}`
                          ) : (
                            <AddInstrumentationButton
                              instrumentationSnippet={item.instrumentation_snippet}
                              dashboardRk={item.rk}
                              dashboardName={item.dashboard}
                            />
                          )}
                        </span>
                      ),
                      id: 4,
                      width: 300,
                      sort: {
                        sortSearchKey: SEARCH_PARAMS.SORT_BY,
                        sortOrderSearchKey: SEARCH_PARAMS.SORT,
                      },
                    },
                    {
                      label: "Views",
                      uid: "views",
                      render: (item: BiReport) => (
                        <span className="al-text-xs al-text-gray-800 al-font-normal">
                          {item.views}
                        </span>
                      ),
                      id: 5,
                      sort: {
                        sortSearchKey: SEARCH_PARAMS.SORT_BY,
                        sortOrderSearchKey: SEARCH_PARAMS.SORT,
                      },
                    },
                    {
                      label: "Unique Users",
                      uid: "user_count",
                      render: (item: BiReport) => (
                        <span className="al-text-xs al-text-gray-800 al-font-normal">
                          {item.user_count}
                        </span>
                      ),
                      id: 6,
                      sort: {
                        sortSearchKey: SEARCH_PARAMS.SORT_BY,
                        sortOrderSearchKey: SEARCH_PARAMS.SORT,
                      },
                    },
                    {
                      label: "Refreshes",
                      uid: "refreshes",
                      render: (item: BiReport) => (
                        <span className="al-text-xs al-text-gray-800 al-font-normal">
                          {item.refreshes}
                        </span>
                      ),
                      id: 7,
                      sort: {
                        sortSearchKey: SEARCH_PARAMS.SORT_BY,
                        sortOrderSearchKey: SEARCH_PARAMS.SORT,
                      },
                    },
                    {
                      label: "Workbook",
                      uid: "workbook",
                      render: (item: BiReport) => (
                        <span className="al-text-xs al-text-gray-800 al-font-normal">
                          {item.workbook}
                        </span>
                      ),
                      id: 8,
                      width: 200,
                      filter: !isReportsFiltersLoading
                        ? {
                            filterType: FilterType.DROPDOWN,
                            searchKey: SEARCH_PARAMS.WORKBOOK,
                            value: searchParams.getAll(SEARCH_PARAMS.WORKBOOK) || [],
                            options: reportsFilters?.workbook
                              ? reportsFilters.workbook.map((w: string) => ({
                                  label: w,
                                  value: w,
                                }))
                              : [],
                            onChange: (value: string[]) => {
                              updateSearchParamsByKey({
                                [SEARCH_PARAMS.WORKBOOK]: value,
                                [SEARCH_PARAMS.PAGE]: "1",
                              });
                            },
                          }
                        : undefined,
                    },
                    {
                      label: "Site Name",
                      uid: "site_name",
                      render: (item: BiReport) => (
                        <ColorInsightTags onClick={() => {}} tags={[item.site_name]} />
                      ),
                      id: 9,
                      filter: !isReportsFiltersLoading
                        ? {
                            filterType: FilterType.DROPDOWN,
                            searchKey: SEARCH_PARAMS.SITE_NAME,
                            value: searchParams.getAll(SEARCH_PARAMS.SITE_NAME) || [],
                            options: reportsFilters?.site_name
                              ? reportsFilters.site_name.map((s: string) => ({
                                  label: s,
                                  value: s,
                                }))
                              : [],
                            onChange: (value: string[]) => {
                              updateSearchParamsByKey({
                                [SEARCH_PARAMS.SITE_NAME]: value,
                                [SEARCH_PARAMS.PAGE]: "1",
                              });
                            },
                          }
                        : undefined,
                    },
                    {
                      label: "Instance Name",
                      uid: "instance_name",
                      render: (item: BiReport) => (
                        <span className="al-text-xs al-text-gray-800 al-font-normal">
                          {item.instance_name}
                        </span>
                      ),
                      id: 9,
                      filter: !isReportsFiltersLoading
                        ? {
                            filterType: FilterType.DROPDOWN,
                            searchKey: SEARCH_PARAMS.INSTANCE_NAME,
                            value: searchParams.getAll(SEARCH_PARAMS.INSTANCE_NAME) || [],
                            options: reportsFilters?.instance_name
                              ? reportsFilters.instance_name.map((s: string) => ({
                                  label: s,
                                  value: s,
                                }))
                              : [],
                            onChange: (value: string[]) => {
                              updateSearchParamsByKey({
                                [SEARCH_PARAMS.INSTANCE_NAME]: value,
                                [SEARCH_PARAMS.PAGE]: "1",
                              });
                            },
                          }
                        : undefined,
                    },
                    {
                      label: "Tableau Tags",
                      uid: "tableauTags",
                      render: (item: BiReport) => (
                        <ColorInsightTags onClick={() => {}} tags={item?.tableauTags || []} />
                      ),
                      id: 10,
                      filter: !isReportsFiltersLoading
                        ? {
                            filterType: FilterType.DROPDOWN,
                            searchKey: SEARCH_PARAMS.TABLEAU_TAGS,
                            value: searchParams.getAll(SEARCH_PARAMS.TABLEAU_TAGS) || [],
                            options: reportsFilters?.tableauTags
                              ? reportsFilters.tableauTags.map((tag: string) => ({
                                  label: tag,
                                  value: tag,
                                }))
                              : [],
                            onChange: (value: string[]) => {
                              updateSearchParamsByKey({
                                [SEARCH_PARAMS.TABLEAU_TAGS]: value,
                                [SEARCH_PARAMS.PAGE]: "1",
                              });
                            },
                          }
                        : undefined,
                    },
                  ]}
                  data={paginationResponse}
                  resetPage={() => {}}
                  columnPickerPortal="columnpicker-container"
                  minWidth={2500}
                  defaultSort={{ sortParamValue: "views", order: "desc" }}
                />
                <Paginate
                  itemCount={paginationResponse.total || 0}
                  page={(paginationResponse.page || 1) - 1}
                  pageSize={paginationResponse.size || 20}
                  numPages={paginationResponse.pages || 0}
                  onPageClick={page => {
                    updateSearchParamsByKey({
                      [SEARCH_PARAMS.PAGE]: `${page + 1}`,
                    });
                  }}
                  onPageSizeChange={size =>
                    updateSearchParamsByKey({
                      [SEARCH_PARAMS.PAGE_SIZE]: `${size}`,
                    })
                  }
                  showPageSize
                />
              </>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default BiReportsList;
